import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ShieldAlert, TrendingUp, AlertTriangle, Users, Banknote, Activity } from "lucide-react";

export const IntegrityCentre = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["integrity"],
    queryFn: async () => (await api.get("/integrity/anomalies")).data,
  });

  if (isLoading || !data) return <div className="text-[#B3C2CC]">Compiling integrity analytics…</div>;

  const risk = data.risk_score || 0;
  const riskLabel = risk >= 60 ? "elevated" : risk >= 30 ? "moderate" : "nominal";
  const riskColour = risk >= 60 ? "#A93F3F" : risk >= 30 ? "#C99A2E" : "#2F6B4F";

  return (
    <div className="space-y-6" data-testid="page-integrity">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Integrity & Anti-Corruption Centre</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
            <ShieldAlert className="text-[#C99A2E]" /> National Integrity Console
          </h1>
          <div className="text-[13px] text-[#B3C2CC] mt-1">Automated anomaly detection across applications, ownership, revenue and officer activity.</div>
        </div>
        <div className="cad-panel px-5 py-3 text-right">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5]">National risk score</div>
          <div className="flex items-center gap-3 justify-end mt-1">
            <div className="cad-kpi-value text-4xl" style={{ color: riskColour }}>{risk}</div>
            <span className="cad-chip" style={{ borderColor: riskColour, color: riskColour }}>{riskLabel}</span>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          ["Rapid approvals", data.counts.rapid_approvals, TrendingUp],
          ["Manual overrides", data.counts.manual_overrides, AlertTriangle],
          ["Conflict reviews", data.counts.conflict_review_mineral_rights, ShieldAlert],
          ["High concentration", data.counts.high_concentration_holders, Users],
          ["Overdue payers", data.counts.overdue_payers, Banknote],
        ].map(([label, value, Icon]) => (
          <div key={label} className="cad-panel p-4" data-testid={`integrity-${label}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5]">{label}</div>
                <div className="cad-kpi-value text-3xl mt-1 text-[#F6F8FA]">{value}</div>
              </div>
              <Icon size={18} className="text-[#C99A2E]" />
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] flex items-center gap-1.5">
            <Users size={12}/> Beneficial concentration
          </div>
          <div className="font-display font-bold text-[#F6F8FA] mt-1">Top licence holders</div>
          <div className="mt-3 space-y-2">
            {data.beneficial_concentration.map((c, i) => (
              <div key={i} className="border-b cad-hairline pb-2 last:border-none">
                <div className="flex items-center justify-between">
                  <div className="text-[13px] text-[#F6F8FA]">{c.applicant}</div>
                  <div className="font-mono-cad text-[13px] text-[#C99A2E]">{c.licences}</div>
                </div>
                <div className="text-[11px] text-[#B3C2CC] font-mono-cad mt-0.5">
                  {c.commodities} commodities · {c.provinces} provinces · {c.area_ha.toLocaleString()} ha
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] flex items-center gap-1.5">
            <Banknote size={12}/> Revenue leakage
          </div>
          <div className="font-display font-bold text-[#F6F8FA] mt-1">Overdue payer list</div>
          <div className="mt-3 space-y-2">
            {data.revenue_leakage.map((r, i) => (
              <div key={i} className="border-b cad-hairline pb-2 last:border-none flex items-center justify-between">
                <div>
                  <div className="text-[13px] text-[#F6F8FA]">{r.payer}</div>
                  <div className="text-[11px] text-[#B3C2CC] font-mono-cad">{r.invoices} unpaid invoices</div>
                </div>
                <div className="font-mono-cad text-[14px] text-[#E67E7E]">${r.amount.toLocaleString()}</div>
              </div>
            ))}
            {data.revenue_leakage.length === 0 && (
              <div className="text-[12px] text-[#7C93A5] italic">No leakage detected.</div>
            )}
          </div>
        </div>
      </div>

      <div className="cad-panel p-5">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] flex items-center gap-1.5">
          <Activity size={12}/> Officer activity
        </div>
        <table className="w-full text-[13px] mt-3">
          <thead>
            <tr className="text-left border-b cad-hairline">
              {["Officer","Actions","Approvals","Approval ratio"].map((h) => (
                <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="cad-tbody">
            {data.officer_activity.map((o, i) => {
              const ratio = o.actions ? (o.approvals / o.actions * 100).toFixed(0) : 0;
              return (
                <tr key={i} data-testid={`officer-${i}`}>
                  <td className="px-3 py-2 text-[#F6F8FA]">{o.officer}</td>
                  <td className="px-3 py-2 font-mono-cad">{o.actions}</td>
                  <td className="px-3 py-2 font-mono-cad">{o.approvals}</td>
                  <td className="px-3 py-2 font-mono-cad">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 bg-[#18232D] rounded-full overflow-hidden">
                        <div className="h-full" style={{ width: `${ratio}%`, background: "#C99A2E" }} />
                      </div>
                      <span>{ratio}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="cad-panel p-5">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Governance rule</div>
        <div className="text-[13px] text-[#B3C2CC] mt-2 leading-relaxed">
          Signals shown here are advisory. Investigations must follow statutory procedures.
          Every original evidence record is preserved with its immutable audit chain.
        </div>
      </div>
    </div>
  );
};
